import { BLOCKS_BY_TYPE } from '@docgen/blocks';

/** Where a drag would insert, from direct DOM hit-testing. */
export interface DropTarget {
  pageId: string;
  /** null → insert at page level, else the container block's id */
  parentBlockId: string | null;
  index: number;
  /** indicator line rect, in VIEWPORT coordinates (render position:fixed) */
  indicator: { top: number; left: number; width: number; height: number };
}

/**
 * Hit-test a pointer position against the rendered document. Finds the
 * innermost container under the pointer (page level otherwise) and the
 * insertion index from child midpoints.
 */
export function probeDropTarget(
  x: number,
  y: number,
  scopeEl: HTMLElement,
  blockType: string,
): DropTarget | null {
  if (!BLOCKS_BY_TYPE.has(blockType)) return null;
  const el = document.elementFromPoint(x, y);
  if (!el || !scopeEl.contains(el)) return null;
  const pageEl = el.closest<HTMLElement>('[data-page]');
  if (!pageEl) return null;
  const pageId = pageEl.dataset['page'] ?? '';

  let containerEl: HTMLElement | null = el.closest<HTMLElement>('[data-container]');
  const containerId = containerEl?.dataset['block'] ?? null;
  if (!containerEl) containerEl = pageEl;

  const rows = Array.from(containerEl.querySelectorAll<HTMLElement>('[data-key]'))
    .filter(
      (child) =>
        (child.parentElement?.closest<HTMLElement>('[data-container],[data-page]') ?? null) ===
        containerEl,
    )
    .map((child) => ({ el: child, rect: child.getBoundingClientRect() }));

  let index = rows.length;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    if (y < row.rect.top + row.rect.height / 2) {
      index = i;
      break;
    }
  }

  const containerRect = containerEl.getBoundingClientRect();
  const before = rows[index];
  const after = rows[index - 1];
  const indicatorTop = before
    ? before.rect.top - 2
    : after
      ? after.rect.top + after.rect.height + 2
      : containerRect.top + 8;
  return {
    pageId,
    parentBlockId: containerId,
    index,
    indicator: {
      top: indicatorTop,
      left: containerRect.left + 8,
      width: containerRect.width - 16,
      height: 3,
    },
  };
}
